/**
 * Pachisi Board Component
 *
 * Authentic Pachisi board with cross-shaped layout on a 15×15 CSS grid.
 * - Cross arms: 3 columns × 6 rows each
 * - Center (Charkoni): 3×3 — start/finish area for all players
 * - 68 track positions mapped around the cross perimeter
 * - 12 castle/safe squares marked with ✕
 * - Home stretches: middle column of each arm (5 cells leading to center)
 * - No corner home bases (unlike Ludo) — pawns start from center
 *
 * Player colors and arm assignments:
 *   Red = bottom arm, Blue = right arm, Green = top arm, Yellow = left arm
 *
 * Track flows counter-clockwise around the outer perimeter of the cross.
 */

import React, { useMemo } from 'react';
import {
  PlayerColor,
  PawnIdentifier,
  SAFE_SQUARES,
  PLAYER_START_POSITIONS,
  PLAYER_COLORS,
} from '../types';
import { PachisiEngine } from '../PachisiEngine';
import './PachisiBoard.css';

interface PachisiBoardProps {
  engine: PachisiEngine;
  onPawnSelect: (player: PlayerColor, pawnIndex: number) => void;
  highlightedPawns: PawnIdentifier[];
  currentTurn: PlayerColor;
  isInteractive: boolean;
  playerCount: number;
  gamePhase: 'waiting' | 'throwing' | 'moving' | 'animating' | 'completed';
}

// ============================================================================
// Board Layout (15×15 grid)
// ============================================================================
//
// Cross shape:
//   Top arm:    rows 0-5,  cols 6-8
//   Left arm:   rows 6-8,  cols 0-5
//   Center:     rows 6-8,  cols 6-8
//   Right arm:  rows 6-8,  cols 9-14
//   Bottom arm: rows 9-14, cols 6-8
//
// The track uses the outer 2 columns of each arm (left & right for vertical
// arms, top & bottom for horizontal arms). The middle column/row is the
// home stretch for that arm's player.
//
// Track connectivity: Arms connect at the center junction cells.
// After going up one arm's outer column, the track crosses through
// the center row/col to reach the next arm.

/**
 * Build the 52-cell visual track path around the cross perimeter.
 * The path is continuous and counter-clockwise.
 *
 * Starting from Red's entry (exits center going down the left column of bottom arm):
 *
 * 1. Bottom arm left col DOWN: [9,6]→[14,6]  (6 cells)
 * 2. Bottom arm tip RIGHT: [14,7],[14,8]      (2 cells)
 * 3. Bottom arm right col UP: [13,8]→[9,8]   (5 cells)
 * 4. Center row transition RIGHT: [8,9]       (1 cell — enters right arm)
 * 5. Right arm top row RIGHT: [6,9]→[6,14]   (6 cells) — WAIT, need to go from [8,9] to [6,9]
 *
 * Actually the correct flow after bottom arm right col ends at [9,8]:
 * The track needs to go from the bottom-right junction into the right arm.
 * In a Pachisi board, the track wraps around each arm's outer edge.
 *
 * Let me trace it properly:
 * After coming UP the right column of the bottom arm (ending at [9,8]),
 * the track turns RIGHT along the bottom row of the right arm.
 *
 * Correct continuous path (counter-clockwise when viewed from above):
 */
const VISUAL_TRACK: [number, number][] = (() => {
  const path: [number, number][] = [];

  // === SEGMENT 1: Bottom arm (Red's territory) ===
  // Exit center going down left column
  path.push([9, 6], [10, 6], [11, 6], [12, 6], [13, 6], [14, 6]); // 0-5
  // Turn right at bottom tip
  path.push([14, 7], [14, 8]); // 6-7
  // Come back up right column
  path.push([13, 8], [12, 8], [11, 8], [10, 8], [9, 8]); // 8-12

  // === SEGMENT 2: Right arm (Blue's territory) ===
  // Continue into right arm along bottom row (row 8)
  path.push([8, 9], [8, 10], [8, 11], [8, 12], [8, 13], [8, 14]); // 13-18
  // Turn up at right tip
  path.push([7, 14], [6, 14]); // 19-20
  // Come back left along top row (row 6)
  path.push([6, 13], [6, 12], [6, 11], [6, 10], [6, 9]); // 21-25

  // === SEGMENT 3: Top arm (Green's territory) ===
  // Continue into top arm going up right column (col 8)
  path.push([5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8]); // 26-31
  // Turn left at top tip
  path.push([0, 7], [0, 6]); // 32-33
  // Come back down left column (col 6)
  path.push([1, 6], [2, 6], [3, 6], [4, 6], [5, 6]); // 34-38

  // === SEGMENT 4: Left arm (Yellow's territory) ===
  // Continue into left arm along top row (row 6)
  path.push([6, 5], [6, 4], [6, 3], [6, 2], [6, 1], [6, 0]); // 39-44
  // Turn down at left tip
  path.push([7, 0], [8, 0]); // 45-46
  // Come back right along bottom row (row 8)
  path.push([8, 1], [8, 2], [8, 3], [8, 4], [8, 5]); // 47-51

  // Total: 52 visual cells ✓
  // Path connects back to start: after [8,5], next would be [9,6] (position 0)
  return path;
})();

/**
 * Map logical track position (0-67) to visual grid cell [row, col].
 *
 * 68 logical positions distributed across 52 visual cells.
 * Each arm section (17 logical positions) maps to 13 visual cells.
 * We distribute the extra positions evenly across the straight sections.
 */
function getTrackVisualPosition(trackPos: number): [number, number] {
  // Each player's section is 17 positions (17 × 4 = 68)
  // Each visual arm section is 13 cells (13 × 4 = 52)
  const sectionIndex = Math.floor(trackPos / 17); // 0-3
  const posInSection = trackPos % 17;

  // Map 17 logical positions to 13 visual cells
  // Strategy: positions 0-5 map to visual 0-5 (1:1, the outgoing column)
  //           positions 6-7 map to visual 6-7 (1:1, the tip turn)
  //           positions 8-16 map to visual 8-12 (9 positions → 5 cells, compressed)
  // This puts extra density on the return path, which is fine visually.

  let visualOffset: number;
  if (posInSection <= 5) {
    // Outgoing column: 1:1 mapping
    visualOffset = posInSection;
  } else if (posInSection <= 7) {
    // Tip turn: 1:1 mapping
    visualOffset = posInSection;
  } else {
    // Return path + transition: 9 positions → 5 cells
    // positions 8-16 → visual 8-12
    visualOffset = 8 + Math.floor(((posInSection - 8) * 5) / 9);
  }

  const visualIndex = (sectionIndex * 13 + visualOffset) % 52;
  return VISUAL_TRACK[visualIndex];
}

// Build the full track position map
const TRACK_POSITIONS: Map<number, [number, number]> = new Map();
for (let i = 0; i < 68; i++) {
  TRACK_POSITIONS.set(i, getTrackVisualPosition(i));
}

// Reverse lookup: visual cell key → first track position at that cell
const CELL_FIRST_TRACK_POS: Map<string, number> = new Map();
for (let i = 0; i < 68; i++) {
  const [r, c] = getTrackVisualPosition(i);
  const key = `${r}-${c}`;
  if (!CELL_FIRST_TRACK_POS.has(key)) {
    CELL_FIRST_TRACK_POS.set(key, i);
  }
}

// Home stretch positions (middle column/row of each arm, leading toward center)
// The engine uses HOME_COLUMN_LENGTH = 7, so we provide 7 positions.
// Positions start one cell in from the arm tip (tip is part of the track)
// and end at the center boundary.
const HOME_STRETCH_POSITIONS: Record<PlayerColor, [number, number][]> = {
  // Red: bottom arm middle column (col 7), rows 13→7 going up toward center
  red: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7], [8, 7], [7, 7]],
  // Blue: right arm middle row (row 7), cols 13→7 going left toward center
  blue: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9], [7, 8], [7, 7]],
  // Green: top arm middle column (col 7), rows 1→7 going down toward center
  green: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7], [7, 7]],
  // Yellow: left arm middle row (row 7), cols 1→7 going right toward center
  yellow: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6], [7, 7]],
};

// Home base pawn positions within the Charkoni (center 3×3)
// In Pachisi, all pawns start from the center
const HOME_BASE_POSITIONS: Record<PlayerColor, [number, number][]> = {
  red: [[8, 6], [8, 8], [7, 6], [6, 6]],
  blue: [[6, 8], [8, 8], [6, 6], [8, 6]],
  green: [[6, 6], [6, 8], [8, 6], [8, 8]],
  yellow: [[7, 6], [7, 8], [6, 6], [8, 8]],
};

// ============================================================================
// Helper Functions
// ============================================================================

function isCrossCell(row: number, col: number): boolean {
  if (row >= 0 && row <= 5 && col >= 6 && col <= 8) return true;   // Top arm
  if (row >= 6 && row <= 8 && col >= 0 && col <= 5) return true;   // Left arm
  if (row >= 6 && row <= 8 && col >= 6 && col <= 8) return true;   // Center
  if (row >= 6 && row <= 8 && col >= 9 && col <= 14) return true;  // Right arm
  if (row >= 9 && row <= 14 && col >= 6 && col <= 8) return true;  // Bottom arm
  return false;
}

function isCenterCell(row: number, col: number): boolean {
  return row >= 6 && row <= 8 && col >= 6 && col <= 8;
}

function getHomeStretchInfo(row: number, col: number): { player: PlayerColor; index: number } | null {
  for (const color of PLAYER_COLORS) {
    const positions = HOME_STRETCH_POSITIONS[color];
    for (let i = 0; i < positions.length; i++) {
      if (positions[i][0] === row && positions[i][1] === col) {
        return { player: color, index: i };
      }
    }
  }
  return null;
}

function getArmOwner(row: number, col: number): PlayerColor | null {
  if (row >= 9 && row <= 14 && col >= 6 && col <= 8) return 'red';
  if (row >= 6 && row <= 8 && col >= 9 && col <= 14) return 'blue';
  if (row >= 0 && row <= 5 && col >= 6 && col <= 8) return 'green';
  if (row >= 6 && row <= 8 && col >= 0 && col <= 5) return 'yellow';
  return null;
}

// ============================================================================
// Component
// ============================================================================

const PachisiBoard: React.FC<PachisiBoardProps> = ({
  engine,
  onPawnSelect,
  highlightedPawns,
  currentTurn,
  isInteractive,
  playerCount,
  gamePhase,
}) => {
  const gameState = engine.getGameState();
  const activePlayers = gameState.activePlayers;

  const highlightedSet = useMemo(() => {
    const set = new Set<string>();
    highlightedPawns.forEach((p) => set.add(`${p.player}-${p.pawnIndex}`));
    return set;
  }, [highlightedPawns]);

  const safeSquareSet = useMemo(() => new Set(SAFE_SQUARES), []);

  // Build track cell lookup: cell key → first track position at that cell
  const trackCellLookup = useMemo(() => CELL_FIRST_TRACK_POS, []);

  // Collect all pawns with their grid positions
  const pawnElements = useMemo(() => {
    const elements: Array<{
      player: PlayerColor;
      pawnIndex: number;
      row: number;
      col: number;
      highlighted: boolean;
    }> = [];

    for (const color of activePlayers) {
      const positions = gameState.pawns[color];
      for (const pawn of positions) {
        let row = -1, col = -1;

        if (pawn.location === 'home_base') {
          const basePos = HOME_BASE_POSITIONS[color][pawn.pawnIndex];
          if (basePos) {
            [row, col] = basePos;
          }
        } else if (pawn.location === 'track' && pawn.trackPosition !== undefined) {
          const pos = TRACK_POSITIONS.get(pawn.trackPosition);
          if (pos) {
            [row, col] = pos;
          }
        } else if (pawn.location === 'home_column' && pawn.homeColumnPosition !== undefined) {
          const hcPositions = HOME_STRETCH_POSITIONS[color];
          if (pawn.homeColumnPosition < hcPositions.length) {
            [row, col] = hcPositions[pawn.homeColumnPosition];
          }
        } else if (pawn.location === 'home') {
          [row, col] = [7, 7]; // Center of Charkoni
        }

        if (row >= 0 && col >= 0) {
          elements.push({
            player: color,
            pawnIndex: pawn.pawnIndex,
            row,
            col,
            highlighted: highlightedSet.has(`${color}-${pawn.pawnIndex}`),
          });
        }
      }
    }

    return elements;
  }, [gameState, activePlayers, highlightedSet]);

  const handlePawnClick = (player: PlayerColor, pawnIndex: number) => {
    if (!isInteractive) return;
    if (highlightedSet.has(`${player}-${pawnIndex}`)) {
      onPawnSelect(player, pawnIndex);
    }
  };

  // Pinwheel color pattern for center cells
  const getCenterColor = (row: number, col: number): PlayerColor | null => {
    if (row === 7 && col === 7) return null; // Very center — special
    if (row === 6 && col === 6) return 'green';
    if (row === 6 && col === 7) return 'green';
    if (row === 6 && col === 8) return 'blue';
    if (row === 7 && col === 6) return 'yellow';
    if (row === 7 && col === 8) return 'blue';
    if (row === 8 && col === 6) return 'yellow';
    if (row === 8 && col === 7) return 'red';
    if (row === 8 && col === 8) return 'red';
    return null;
  };

  const renderGrid = () => {
    const cells: React.ReactNode[] = [];

    for (let r = 0; r < 15; r++) {
      for (let c = 0; c < 15; c++) {
        const key = `${r}-${c}`;
        const isInCross = isCrossCell(r, c);
        const isCenter = isCenterCell(r, c);

        if (!isInCross) {
          cells.push(
            <div
              key={key}
              className="pachisi-board__cell pachisi-board__cell--empty"
              style={{ gridRow: r + 1, gridColumn: c + 1 }}
              aria-hidden="true"
            />
          );
          continue;
        }

        let cellClass = 'pachisi-board__cell';
        let content: React.ReactNode = null;
        let ariaLabel: string | undefined;

        if (isCenter) {
          const centerColor = getCenterColor(r, c);
          cellClass += ' pachisi-board__cell--center';
          if (centerColor) {
            cellClass += ` pachisi-board__cell--center-${centerColor}`;
          }
          if (r === 7 && c === 7) {
            cellClass += ' pachisi-board__cell--center-home';
            ariaLabel = 'Charkoni (Home)';
            content = <span className="pachisi-board__center-star" aria-hidden="true">★</span>;
          } else {
            ariaLabel = `Charkoni ${centerColor || ''}`;
          }
        } else {
          // Check if this is a home stretch cell
          const homeStretch = getHomeStretchInfo(r, c);
          // Check if this is a track cell
          const trackPos = trackCellLookup.get(key);

          if (homeStretch) {
            cellClass += ` pachisi-board__cell--home-stretch pachisi-board__cell--${homeStretch.player}`;
            ariaLabel = `${homeStretch.player} home stretch ${homeStretch.index + 1}`;
            content = (
              <span className="pachisi-board__cell-number" aria-hidden="true">
                H{homeStretch.index + 1}
              </span>
            );
          } else if (trackPos !== undefined) {
            cellClass += ' pachisi-board__cell--track';

            const isSafe = safeSquareSet.has(trackPos);
            if (isSafe) {
              cellClass += ' pachisi-board__cell--safe';
            }

            // Check start positions
            for (const [color, startPos] of Object.entries(PLAYER_START_POSITIONS)) {
              if (trackPos === startPos) {
                cellClass += ` pachisi-board__cell--start pachisi-board__cell--start-${color}`;
              }
            }

            // Check for blocks
            const block = engine.hasBlock(trackPos);
            if (block.blocked) {
              cellClass += ' pachisi-board__cell--blocked';
            }

            ariaLabel = `Track ${trackPos}${isSafe ? ' (castle)' : ''}`;
            content = (
              <>
                {isSafe && (
                  <span className="pachisi-board__castle-mark" aria-hidden="true">✕</span>
                )}
                <span className="pachisi-board__cell-number" aria-hidden="true">
                  {trackPos}
                </span>
              </>
            );
          } else {
            // Arm cell that's neither track nor home stretch
            // This can happen for cells that are in the cross but not mapped
            const armOwner = getArmOwner(r, c);
            cellClass += ' pachisi-board__cell--arm';
            if (armOwner) {
              cellClass += ` pachisi-board__cell--arm-${armOwner}`;
            }
          }
        }

        cells.push(
          <div
            key={key}
            className={cellClass}
            style={{ gridRow: r + 1, gridColumn: c + 1 }}
            data-row={r}
            data-col={c}
            data-track-pos={trackCellLookup.get(key)}
            aria-label={ariaLabel}
          >
            {content}
          </div>
        );
      }
    }

    return cells;
  };

  const renderPawns = () => {
    const positionGroups = new Map<string, typeof pawnElements>();
    for (const pawn of pawnElements) {
      const key = `${pawn.row}-${pawn.col}`;
      if (!positionGroups.has(key)) {
        positionGroups.set(key, []);
      }
      positionGroups.get(key)!.push(pawn);
    }

    const rendered: React.ReactNode[] = [];

    positionGroups.forEach((pawns) => {
      pawns.forEach((pawn, stackIndex) => {
        const offset = pawns.length > 1 ? (stackIndex - (pawns.length - 1) / 2) * 25 : 0;
        const isClickable = isInteractive && pawn.highlighted;

        rendered.push(
          <div
            key={`pawn-${pawn.player}-${pawn.pawnIndex}`}
            className={`pachisi-board__pawn pachisi-board__pawn--${pawn.player}${
              pawn.highlighted ? ' pachisi-board__pawn--highlighted' : ''
            }${isClickable ? ' pachisi-board__pawn--clickable' : ''}`}
            style={{
              gridRow: pawn.row + 1,
              gridColumn: pawn.col + 1,
              transform: `translate(${offset}%, ${-offset * 0.5}%)`,
              zIndex: pawn.highlighted ? 10 : 5,
            }}
            onClick={() => handlePawnClick(pawn.player, pawn.pawnIndex)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && isClickable) {
                e.preventDefault();
                handlePawnClick(pawn.player, pawn.pawnIndex);
              }
            }}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            aria-label={`${pawn.player} pawn ${pawn.pawnIndex + 1}${
              pawn.highlighted ? ' (can move)' : ''
            }`}
          >
            <span className="pachisi-board__pawn-inner" aria-hidden="true" />
          </div>
        );
      });
    });

    return rendered;
  };

  return (
    <div className="pachisi-board" role="grid" aria-label="Pachisi board">
      <div className="pachisi-board__grid">
        {renderGrid()}
        {renderPawns()}
      </div>
    </div>
  );
};

export { PachisiBoard };
export default PachisiBoard;
